import { Fragment, useMemo, useState, useRef, useEffect } from "react";
import { Listbox, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Common, CustomScrollbar, Icon } from "@/components";
import ListBoxOption from "@/components/common/ListBox/ListBoxOption";
import { Driver } from "../models";
import InviteDriversDialog from "./InviteDriversDialog";

export const DriverListBox = ({
  drivers,
  selectedDriverId,
  onChange,
  onRefresh,
  loading,
  importSessionId,
}: {
  drivers: Driver[];
  selectedDriverId: string;
  onChange: (driverId: string) => void;
  onRefresh?: () => void;
  loading?: boolean;
  importSessionId: string;
}) => {
  const [query, setQuery] = useState("");
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isListboxOpen, setIsListboxOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedDriver = drivers.find(
    (driver) => driver.id === selectedDriverId
  );

  // Focus the search input when the listbox opens
  useEffect(() => {
    if (isListboxOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isListboxOpen]);

  const filteredDrivers = useMemo(() => {
    return drivers.filter((driver) => {
      const fullName = `${driver.givenName} ${driver.lastName}`;
      return (
        fullName.toLowerCase().includes(query.toLowerCase()) ||
        driver.email.toLowerCase().includes(query.toLowerCase())
      );
    });
  }, [query, drivers]);

  return (
    <>
      <Listbox
        as="div"
        className="relative flex-shrink-0 group block w-full"
        value={selectedDriverId}
        onChange={onChange}
      >
        {() => (
          <>
            <Listbox.Button
              className={clsx(
                "relative w-full h-9 inline-flex items-center justify-center rounded bg-white py-2 pl-3 pr-10 text-left border border-neutral-20 focus:outline-none cursor-pointer"
              )}
            >
              {selectedDriver ? (
                <span className="block truncate">
                  {selectedDriver.givenName} {selectedDriver.lastName}{" "}
                  <span className="text-neutral-60">
                    ({selectedDriver.email})
                  </span>
                </span>
              ) : (
                <span className="block truncate text-neutral-60">
                  Search driver
                </span>
              )}
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <Icon.AngleDown
                  className="stroke-neutral-60"
                  aria-hidden="true"
                  iconType="stroke"
                  size={12}
                />
              </span>
            </Listbox.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              afterEnter={() => setIsListboxOpen(true)}
              afterLeave={() => setIsListboxOpen(false)}
            >
              <Listbox.Options
                className={clsx(
                  "origin-top-right absolute right-0 w-full overflow-auto rounded-md shadow-modal bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10 transform opacity-100 scale-100 mt-2 top-full"
                )}
              >
                <div className="sticky top-0 bg-white p-2 border-b border-neutral-10 z-10">
                  <div className="flex items-center gap-1">
                    <div className="flex-grow min-w-0 w-full">
                      <Common.Input
                        ref={searchInputRef}
                        iconLeft={Icon.Search}
                        iconType="stroke"
                        placeholder="Search driver"
                        value={query}
                        onChange={(e: any) => setQuery(e.target.value)}
                        onClear={() => setQuery("")}
                        onKeyDown={(e) => {
                          if (e.key === " ") {
                            // Prevent default space key behavior from Listbox
                            e.stopPropagation();
                          }
                        }}
                      />
                    </div>
                    {onRefresh && (
                      <Common.Button
                        variant="outline"
                        color="transparent"
                        iconLeft={loading ? Icon.Loading : Icon.Refresh}
                        iconType="fill"
                        onClick={onRefresh}
                        disabled={loading}
                        title={
                          loading ? "Loading drivers..." : "Refresh driver list"
                        }
                        className="flex-shrink-0"
                      />
                    )}
                  </div>
                </div>
                <CustomScrollbar autoHeight>
                  <div className="relative">
                    {loading && (
                      <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
                        <Icon.Loading size={24} />
                      </div>
                    )}
                    {filteredDrivers.length > 0 ? (
                      filteredDrivers.map((driver) => (
                        <ListBoxOption
                          key={driver.id}
                          value={driver.id}
                          content={(selected) => {
                            return (
                              <>
                                <span
                                  className={`block truncate ${
                                    selected ? "font-medium" : "font-normal"
                                  }`}
                                >
                                  {driver.givenName} {driver.lastName}{" "}
                                  <span className="text-neutral-60">
                                    ({driver.email})
                                  </span>
                                </span>
                                {selected ? (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                    <Icon.Checked
                                      iconType="fill"
                                      size={16}
                                      className="fill-neutral-60"
                                    />
                                  </span>
                                ) : null}
                              </>
                            );
                          }}
                        />
                      ))
                    ) : (
                      <div className="p-4 text-center text-neutral-50">
                        {loading ? "Loading drivers..." : "No drivers found"}
                      </div>
                    )}
                  </div>
                </CustomScrollbar>
                <div className="sticky bottom-0 bg-white p-2 border-t border-neutral-10 z-10 flex justify-center">
                  <Common.Button
                    variant="outline"
                    color="blue"
                    iconLeft={Icon.PlusBorder}
                    iconType="fill"
                    onClick={() => setIsInviteDialogOpen(true)}
                    disabled={loading}
                  >
                    Invite Drivers
                  </Common.Button>
                </div>
              </Listbox.Options>
            </Transition>
          </>
        )}
      </Listbox>

      <InviteDriversDialog
        open={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        importSessionId={importSessionId}
      />
    </>
  );
};
